// P1-002: Hardware configuration data — 8 items matching GDD SS4.2.2
import { HardwareType } from '@/shared/types/common';
import type { HardwareConfig, HardwareItem } from '../types/hardware-internal';

// ─────────────────────────────────────────────────────
// Hardware catalog (shop items)
// ─────────────────────────────────────────────────────

export const HARDWARE_CONFIGS: Record<string, HardwareConfig> = {
  'cpu-basic': {
    id: 'cpu-basic',
    name: 'CPU-基础',
    type: HardwareType.CPU,
    compute: 2,
    power: 1,
    cooling: 1, // heat generated = 1
    price: 100,
  },
  'gpu-entry': {
    id: 'gpu-entry',
    name: 'GPU-入门',
    type: HardwareType.GPU,
    compute: 5,
    power: 3,
    cooling: 4, // heat generated = 4
    price: 500,
  },
  'gpu-high': {
    id: 'gpu-high',
    name: 'GPU-高端',
    type: HardwareType.GPU,
    compute: 12,
    power: 7,
    cooling: 10, // heat generated = 10
    price: 2000,
  },
  'tpu': {
    id: 'tpu',
    name: 'TPU',
    type: HardwareType.TPU,
    compute: 8,
    power: 2,
    cooling: 3, // heat generated = 3
    price: 1500,
    specialEffect: { type: 'REDUCE_CD', value: 1 },
  },
  'cooler-air': {
    id: 'cooler-air',
    name: '散热器-风冷',
    type: HardwareType.COOLER,
    compute: 0,
    power: 0,
    cooling: 5, // provides 5 cooling
    price: 200,
  },
  'cooler-water': {
    id: 'cooler-water',
    name: '散热器-水冷',
    type: HardwareType.COOLER,
    compute: 0,
    power: 1,
    cooling: 12, // provides 12 cooling
    price: 800,
    specialEffect: { type: 'REDUCE_OVERLOAD_THRESHOLD', value: 20 },
  },
  'psu-standard': {
    id: 'psu-standard',
    name: '电源-标准',
    type: HardwareType.PSU,
    compute: 0,
    power: 8, // provides 8 power limit
    cooling: 0,
    price: 300,
    isPSU: true,
  },
  'psu-high': {
    id: 'psu-high',
    name: '电源-高功率',
    type: HardwareType.PSU,
    compute: 0,
    power: 15, // provides 15 power limit
    cooling: 0,
    price: 800,
    isPSU: true,
  },
};

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────

let instanceCounter = 0;

/** Create a HardwareItem instance from a config */
export function createHardwareItem(configId: string): HardwareItem {
  const config = HARDWARE_CONFIGS[configId];
  if (!config) {
    throw new Error(`Unknown hardware config: ${configId}`);
  }
  instanceCounter += 1;
  return {
    instanceId: `${configId}-${instanceCounter}`,
    configId: config.id,
    name: config.name,
    type: config.type,
    compute: config.compute,
    power: config.power,
    cooling: config.cooling,
    price: config.price,
    specialEffect: config.specialEffect,
    isPSU: config.isPSU,
  };
}

/** Reset the counter (for tests) */
export function _resetInstanceCounter(): void {
  instanceCounter = 0;
}

// ─────────────────────────────────────────────────────
// FTUE initial hardware: CPU x3, GPU-入门 x1, 风冷 x1, 电源-标准 x1
// ─────────────────────────────────────────────────────

export interface InitialHardwareEntry {
  configId: string;
  count: number;
}

export const INITIAL_HARDWARE: InitialHardwareEntry[] = [
  { configId: 'cpu-basic', count: 3 },
  { configId: 'gpu-entry', count: 1 },
  { configId: 'cooler-air', count: 1 },
  { configId: 'psu-standard', count: 1 },
];

/** Create all initial hardware items */
export function createInitialHardware(): HardwareItem[] {
  const items: HardwareItem[] = [];
  for (const entry of INITIAL_HARDWARE) {
    for (let i = 0; i < entry.count; i++) {
      items.push(createHardwareItem(entry.configId));
    }
  }
  return items;
}
